// Shared theme vocabulary for themed-surface components (RCThemedBox, RCCallout).
// This is the TypeScript counterpart to the `themed-surface` SCSS mixin: the
// types describe the valid theme/variant combinations, and `themeModifier`
// builds the matching BEM modifier class for a given block.
//
// The vocabularies are generated from classes-utility/themed-surface.presets.json — the same
// source that declares them to tools/mixins/themed-surface.scss, which emits the very classes
// `themeModifier` names. Re-exported from here so the theme types keep coming from one place.
// Note what is NOT imported: the tempo token names. Those are the Sass side's payload; TypeScript
// needs only the theme's name to build a modifier class, so the source projects keys-only here.
import {
  SurfaceVariant, TempoTheme, ThemeBorder, UtilityColor, UtilityIntensity,
} from '../../styles/generated/types';

export type {
  SurfaceVariant, TempoTheme, ThemeBorder, UtilityColor,
};

// `transparent` is the intensity with no class suffix — `.themed-box--utility-info` IS the
// fill-less form — so the fills are the other three. Derived here rather than declared as a second
// list in the source: the generator's job is values, and "which member is the bare one" is a fact
// about composition that the template literal below already has to express.
type UtilityFill = Exclude<UtilityIntensity, 'transparent'>;

/**
 * A utility theme variant. The bare family (e.g. `info`) is the transparent,
 * accent-only form (no fill — pair with a `border`); a fill suffix adds a
 * background tint (`info-ultralight` / `info-medium` / `info-bold`).
 */
export type UtilityVariant = UtilityColor | `${UtilityColor}-${UtilityFill}`;

// Hand-authored: the three schemes are the discriminant of the union below, not a vocabulary the
// stylesheet also declares. Sass expresses them as three separate mixins, so there is no list on
// that side to drift from.
export type ThemeType = 'utility' | 'surface' | 'tempo';

/**
 * The theme is a top-level discriminated union: `themeType` selects the scheme
 * and narrows the valid `variant`. Omit both for a plain, unthemed element.
 */
export type ThemeProps =
  | { themeType?: undefined; variant?: undefined }
  | { themeType: 'utility'; variant: UtilityVariant }
  | { themeType: 'surface'; variant?: SurfaceVariant }
  | { themeType: 'tempo'; variant: TempoTheme };

/**
 * Builds the theme modifier class for a block, e.g.
 * `themeModifier('rc-callout', 'utility', 'info-bold')` -> `rc-callout--utility-info-bold`.
 * `surface` defaults its variant to `primary`; `utility`/`tempo` require one.
 */
export function themeModifier(
  block: string,
  themeType?: ThemeType,
  variant?: UtilityVariant | SurfaceVariant | TempoTheme,
): string | undefined {
  if (!themeType) return undefined;

  const resolved = themeType === 'surface' ? variant ?? 'primary' : variant;
  return resolved ? `${block}--${themeType}-${resolved}` : undefined;
}
